import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { School, Plus, Pencil, Users, Clock, Loader2, ClipboardEdit, ScrollText, FileText } from "lucide-react";
import { TranscriptDialog } from "@/components/TranscriptDialog";
import { toast } from "sonner";

const SHIFTS = ["Matutino", "Vespertino", "Noturno", "Integral"];
const SHIFT_COLORS = { Matutino: "bg-amber-100 text-amber-700", Vespertino: "bg-blue-100 text-blue-700", Noturno: "bg-indigo-100 text-indigo-700", Integral: "bg-emerald-100 text-emerald-700" };

export default function Classes() {
  const { scopeParams, selectedSchoolId, isSuperadmin, user } = useAuth();
  const [classes, setClasses] = useState(null);
  const [years, setYears] = useState([]);
  const [open, setOpen] = useState(false);
  const [transcriptFor, setTranscriptFor] = useState(null);
  const [declBusy, setDeclBusy] = useState(null);
  const declarations = async (c) => {
    setDeclBusy(c.id);
    try {
      const r = await api.get(`/classes/${c.id}/enrollment-declarations/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `declaracoes_turma_${c.name}.pdf`; a.click();
    } catch (e) {
      const d = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : null;
      toast.error(d || "Falha ao gerar as declarações");
    } finally { setDeclBusy(null); }
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const canEdit = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user.role);

  const fetchAll = () => {
    api.get("/classes", { params: scopeParams }).then((r) => setClasses(r.data));
    api.get("/academic-years", { params: scopeParams }).then((r) => setYears(r.data));
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [selectedSchoolId]);

  const openNew = () => { setEditing(null); setForm({ name: "", grade: "", stage: "", shift: "Matutino", status: "ATIVA", academic_year_id: years[0]?.id || "", teacher_ids: [], subject_ids: [] }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/classes/${editing.id}`, form);
      else await api.post("/classes", form);
      toast.success("Salvo"); setOpen(false); fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (classes === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;
  const needsSchool = isSuperadmin && !selectedSchoolId;

  return (
    <div>
      <PageHeader title="Turmas" subtitle="Turmas por ano letivo, série e turno">
        {canEdit && <Button onClick={openNew} disabled={needsSchool} data-testid="class-create-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Nova Turma</Button>}
      </PageHeader>
      {classes.length === 0 ? <EmptyState icon={School} title="Nenhuma turma cadastrada" /> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {classes.map((c) => (
            <Card key={c.id} data-testid="class-card" className="card-hover border-slate-200 p-5">
              <div className="flex items-start justify-between">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-indigo-600 font-display text-lg font-black text-white">{c.name}</div>
                <StatusBadge status={c.status} />
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{c.grade || `Turma ${c.name}`}</h3>
              <p className="text-xs text-slate-400">{c.stage || "—"}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                <span data-testid="class-shift-badge" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${SHIFT_COLORS[c.shift] || "bg-slate-100 text-slate-600"}`}><Clock className="h-3 w-3" /> {c.shift || "—"}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600"><Users className="h-3 w-3" /> {c.student_count} alunos</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/alunos?class=${c.id}`)} data-testid={`class-students-${c.id}`} className="flex-1">Ver alunos</Button>
                <Button variant="outline" size="sm" onClick={() => navigate(`/quadro-horarios?turma=${c.id}`)} data-testid={`class-schedule-${c.id}`} className="flex-1">Horários</Button>
                <Button size="sm" onClick={() => navigate(`/turmas/${c.id}/notas-faltas`)} data-testid={`class-grades-edit-${c.id}`} className="flex-1 bg-orange-500 hover:bg-orange-600"><ClipboardEdit className="mr-1.5 h-3.5 w-3.5" />Notas e faltas</Button>
                {canEdit && <Button variant="outline" size="sm" onClick={() => setTranscriptFor(c)} data-testid={`class-transcript-${c.id}`} className="flex-1"><ScrollText className="mr-1.5 h-3.5 w-3.5" />Histórico escolar</Button>}
                {canEdit && <Button variant="outline" size="sm" onClick={() => declarations(c)} disabled={declBusy === c.id} data-testid={`class-declarations-${c.id}`} className="flex-1">{declBusy === c.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1.5 h-3.5 w-3.5" />}Declarações</Button>}
                {canEdit && <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`class-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Editar Turma" : "Nova Turma"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome/Código *</Label><Input data-testid="class-name-input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="1001" /></div>
              <div><Label>Série</Label><Input value={form.grade || ""} onChange={(e) => set("grade", e.target.value)} placeholder="6º Ano" /></div>
            </div>
            <div><Label>Etapa/Modalidade</Label><Input value={form.stage || ""} onChange={(e) => set("stage", e.target.value)} placeholder="Ensino Fundamental II" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Turno</Label>
                <Select value={form.shift} onValueChange={(v) => set("shift", v)}>
                  <SelectTrigger data-testid="class-shift-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ATIVA">ATIVA</SelectItem><SelectItem value="INATIVA">INATIVA</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Ano Letivo</Label>
              <Select value={form.academic_year_id || ""} onValueChange={(v) => set("academic_year_id", v)}>
                <SelectTrigger data-testid="class-year-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.year}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving || !form.name} data-testid="class-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <TranscriptDialog cls={transcriptFor} onClose={() => setTranscriptFor(null)} />
    </div>
  );
}
